import { useMemo } from 'react';
import { resolveMediaUrl } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';
import { SiteLevelBadge } from '@/components/SiteLevelBadge';
import { inputClass, labelClass } from '@/components/ui/inputStyles';
import {
  buildWecomHierarchyGroups,
  effectiveWecomCfg,
  hasWecomOverride,
  wecomEnabledSummary,
  type MiniproWecomSite,
  type SiteWecomGroupRow,
} from '@/lib/miniproWecomSite';

const detailsClass =
  'rounded-lg border border-slate-200 bg-white shadow-sm open:ring-1 open:ring-slate-200/80 [&_summary::-webkit-details-marker]:hidden';
const summaryClass =
  'flex cursor-pointer list-none items-center justify-between gap-3 px-3 py-2.5 text-sm hover:bg-slate-50/80';

type Props = {
  sites: MiniproWecomSite[];
  siteIds: number[];
  groups: Record<string, SiteWecomGroupRow>;
  onGroupsChange: (groups: Record<string, SiteWecomGroupRow>) => void;
  onUploadImage: (file: File) => Promise<string>;
  onUploadSuccess?: (msg: string) => void;
  onUploadError?: (msg: string) => void;
};

function WecomFormFields({
  siteId,
  cfg,
  depth,
  inheriting,
  parentSiteName,
  onPatch,
  onUploadImage,
  onUploadSuccess,
  onUploadError,
}: {
  siteId: number;
  cfg: SiteWecomGroupRow;
  depth: 0 | 1;
  inheriting: boolean;
  parentSiteName?: string;
  onPatch: (patch: Partial<SiteWecomGroupRow>) => void;
  onUploadImage: (file: File) => Promise<string>;
  onUploadSuccess?: (msg: string) => void;
  onUploadError?: (msg: string) => void;
}) {
  async function pickQr(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      const path = await onUploadImage(f);
      onPatch({ qr_image_url: path });
      onUploadSuccess?.('站点群二维码已填入（请点击保存写入）');
    } catch (ex: unknown) {
      onUploadError?.(ex instanceof Error ? ex.message : '上传失败');
    }
  }

  async function pickIcon(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    try {
      const path = await onUploadImage(f);
      onPatch({ icon_image_url: path });
      onUploadSuccess?.('站点群图标已填入（请点击保存写入）');
    } catch (ex: unknown) {
      onUploadError?.(ex instanceof Error ? ex.message : '上传失败');
    }
  }

  return (
    <div className="border-t border-slate-100 px-3 pb-4 pt-3">
      {inheriting && depth === 1 && parentSiteName ? (
        <p className="mb-3 rounded-lg border border-teal-200/80 bg-teal-50/90 px-3 py-2 text-xs leading-relaxed text-teal-900">
          当前沿用一级站点「{parentSiteName}」的配置。修改任意项将自动保存为本站单独配置；也可点击下方「恢复沿用一级」。
        </p>
      ) : null}
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="text-xs text-slate-500">ID {siteId}</span>
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={cfg.enabled} onChange={(e) => onPatch({ enabled: e.target.checked })} />
          开启：企业微信群
        </label>
      </div>
      {cfg.enabled ? (
        <div className="grid gap-3 md:grid-cols-2">
          <label className={labelClass}>
            <span>入口类型</span>
            <select
              className={inputClass}
              value={cfg.entry_mode}
              onChange={(e) =>
                onPatch({ entry_mode: (e.target.value as SiteWecomGroupRow['entry_mode']) || 'plugin' })
              }
            >
              <option value="plugin">插件（推荐，企业微信官方）</option>
              <option value="image">图片展示（点击按钮弹出群二维码）</option>
              <option value="webview">H5 链接（web-view）</option>
              <option value="copy">仅复制链接</option>
            </select>
          </label>
          <label className={labelClass}>
            <span>按钮文案</span>
            <input
              className={inputClass}
              value={cfg.button_text}
              onChange={(e) => onPatch({ button_text: e.target.value })}
              placeholder={
                cfg.entry_mode === 'plugin'
                  ? '如：立即入群'
                  : cfg.entry_mode === 'image'
                    ? '如：进群'
                    : '如：进群入口'
              }
            />
          </label>
          <label className={labelClass}>
            <span>引导标题</span>
            <input
              className={inputClass}
              value={cfg.title}
              onChange={(e) => onPatch({ title: e.target.value })}
              placeholder="如：加入企业微信群"
            />
          </label>
          <label className={labelClass}>
            <span>引导副标题（可选）</span>
            <input
              className={inputClass}
              value={cfg.subtitle}
              onChange={(e) => onPatch({ subtitle: e.target.value })}
              placeholder="如：群内实时发布站点最新信息"
            />
          </label>
          <label className={labelClass}>
            <span>
              进群入口链接（https）
              {cfg.entry_mode === 'image' ? (
                <span className="ml-1 font-normal text-slate-500">（图片展示模式下可留空）</span>
              ) : null}
            </span>
            <input
              className={inputClass}
              value={cfg.entry_url}
              onChange={(e) => onPatch({ entry_url: e.target.value })}
              placeholder="https://work.weixin.qq.com/..."
            />
          </label>
          <label className={labelClass}>
            <span>群图标（可选，建议方形）</span>
            <div className="flex flex-wrap items-center gap-2">
              <input
                className={inputClass}
                value={cfg.icon_image_url}
                onChange={(e) => onPatch({ icon_image_url: e.target.value })}
                placeholder="/uploads/... 或 https://..."
              />
              <label className="cursor-pointer whitespace-nowrap rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50">
                上传
                <input type="file" accept="image/*" className="hidden" onChange={(e) => void pickIcon(e)} />
              </label>
            </div>
            {cfg.icon_image_url ? (
              <img
                src={resolveMediaUrl(cfg.icon_image_url)}
                alt=""
                className="mt-2 h-12 w-12 rounded-xl border border-slate-200 object-cover"
              />
            ) : null}
          </label>
          <label className={labelClass}>
            <span>
              群二维码图片
              {cfg.entry_mode === 'image' ? (
                <span className="ml-1 font-normal text-amber-800">（图片展示模式必填）</span>
              ) : (
                <span className="ml-1 font-normal text-slate-500">（可选）</span>
              )}
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <input
                className={inputClass}
                value={cfg.qr_image_url}
                onChange={(e) => onPatch({ qr_image_url: e.target.value })}
                placeholder="/uploads/... 或 https://..."
              />
              <label className="cursor-pointer whitespace-nowrap rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50">
                上传
                <input type="file" accept="image/*" className="hidden" onChange={(e) => void pickQr(e)} />
              </label>
            </div>
            {cfg.qr_image_url ? (
              <img
                src={resolveMediaUrl(cfg.qr_image_url)}
                alt=""
                className="mt-2 h-20 w-20 rounded border border-slate-200 object-cover"
              />
            ) : null}
          </label>
        </div>
      ) : (
        <p className="text-xs text-slate-500">关闭后，该站点信息在小程序详情页顶部不显示进群引导。</p>
      )}
    </div>
  );
}

function normalizeGroupsInput(raw: Record<string, SiteWecomGroupRow> | null | undefined): Record<string, SiteWecomGroupRow> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  return raw;
}

export function SiteWecomGroupsEditor({
  sites,
  siteIds,
  groups: groupsProp,
  onGroupsChange,
  onUploadImage,
  onUploadSuccess,
  onUploadError,
}: Props) {
  const groups = normalizeGroupsInput(groupsProp);
  const sitesById = useMemo(() => new Map(sites.map((s) => [s.id, s])), [sites]);
  const hierarchyGroups = useMemo(() => buildWecomHierarchyGroups(siteIds, sites), [siteIds, sites]);
  const scopeCount = useMemo(() => hierarchyGroups.reduce((n, g) => n + (g.parent ? 1 : 0) + g.children.length, 0), [hierarchyGroups]);

  function patch(siteId: number, patchRow: Partial<SiteWecomGroupRow>) {
    const key = String(siteId);
    const eff = effectiveWecomCfg(groups, siteId, sitesById);
    onGroupsChange({
      ...groups,
      [key]: { ...eff, ...patchRow },
    });
  }

  function clearOverride(siteId: number) {
    if (!hasWecomOverride(groups, siteId)) return;
    const next = { ...groups };
    delete next[String(siteId)];
    onGroupsChange(next);
  }

  function renderSiteBlock(siteId: number, siteName: string, depth: 0 | 1, parentSiteName?: string) {
    const inheriting = depth === 1 && !hasWecomOverride(groups, siteId);
    const cfg = effectiveWecomCfg(groups, siteId, sitesById);
    const summary = wecomEnabledSummary(cfg);
    const override = hasWecomOverride(groups, siteId);

    return (
      <details key={siteId} className={cn(detailsClass, depth === 1 && 'ml-0 border-l-4 border-l-emerald-500/40')}>
        <summary className={summaryClass}>
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <SiteLevelBadge depth={depth} className={depth === 1 ? '!px-1 !py-px !text-[9px]' : undefined} />
            <span className="truncate font-medium text-slate-900">{siteName}</span>
            {depth === 1 ? (
              <span
                className={cn(
                  'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium',
                  inheriting ? 'bg-slate-100 text-slate-600' : 'bg-amber-50 text-amber-900 ring-1 ring-amber-200/80',
                )}
              >
                {inheriting ? '沿用一级' : '已单独配置'}
              </span>
            ) : null}
          </span>
          <span className="shrink-0 text-xs text-slate-500">{summary}</span>
          <span className="shrink-0 text-xs font-normal text-teal-700">展开</span>
        </summary>
        <div className="border-t border-slate-100 px-3 pb-2 pt-1">
          {depth === 1 && override ? (
            <div className="mb-2 flex justify-end">
              <Button type="button" variant="ghost" size="sm" className="!text-teal-800" onClick={() => clearOverride(siteId)}>
                恢复沿用一级
              </Button>
            </div>
          ) : null}
          <WecomFormFields
            siteId={siteId}
            cfg={cfg}
            depth={depth}
            inheriting={inheriting}
            parentSiteName={parentSiteName}
            onPatch={(p) => patch(siteId, p)}
            onUploadImage={onUploadImage}
            onUploadSuccess={onUploadSuccess}
            onUploadError={onUploadError}
          />
        </div>
      </details>
    );
  }

  if (siteIds.length === 0) {
    return <p className="text-sm text-slate-500">请先在上方选择展示站点并保存。</p>;
  }

  if (scopeCount === 0) {
    return <p className="text-sm text-slate-500">展示站点下暂无可配置的站点（请确认已勾选一级或二级站点）。</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2 text-xs text-slate-600">
        <span>
          共 <strong className="text-slate-800">{scopeCount}</strong> 个站点可配置；二级站点默认沿用所属一级，可单独覆盖。
        </span>
        <span className="text-slate-500">默认折叠，点击各站点标题展开编辑</span>
      </div>
      {hierarchyGroups.map((g, gi) => {
        const parentRow = g.parent;
        const parentSite = parentRow?.site;
        const parentId = parentSite?.id ?? 0;
        const parentName = parentSite?.site_name ?? '';
        const parentCfg = parentId > 0 ? effectiveWecomCfg(groups, parentId, sitesById) : null;
        const childCount = g.children.length;

        if (parentRow && parentSite) {
          if (childCount === 0) {
            return renderSiteBlock(parentId, parentSite.site_name, 0);
          }
          return (
            <details key={`g-${gi}-${parentId}`} className={detailsClass}>
              <summary className={cn(summaryClass, 'bg-slate-50/60')}>
                <span className="flex min-w-0 flex-1 items-center gap-2">
                  <SiteLevelBadge depth={0} />
                  <span className="truncate font-semibold text-slate-900">{parentSite.site_name}</span>
                  <span className="shrink-0 rounded-full bg-slate-200/80 px-2 py-0.5 text-[10px] font-medium text-slate-700">
                    含 {childCount} 个二级
                  </span>
                </span>
                {parentCfg ? (
                  <span className="shrink-0 text-xs text-slate-500">{wecomEnabledSummary(parentCfg)}</span>
                ) : null}
                <span className="shrink-0 text-xs font-normal text-teal-700">展开</span>
              </summary>
              <div className="space-y-3 border-t border-slate-100 p-3">
                <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-3">
                  <p className="mb-2 text-xs font-semibold text-slate-700">一级站点（本组默认配置，二级默认沿用）</p>
                  <WecomFormFields
                    siteId={parentId}
                    cfg={parentCfg ?? effectiveWecomCfg(groups, parentId, sitesById)}
                    depth={0}
                    inheriting={false}
                    onPatch={(p) => patch(parentId, p)}
                    onUploadImage={onUploadImage}
                    onUploadSuccess={onUploadSuccess}
                    onUploadError={onUploadError}
                  />
                </div>
                <div className="space-y-2">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">二级站点</p>
                  {g.children.map((c) => renderSiteBlock(c.site.id, c.site.site_name, 1, parentName))}
                </div>
              </div>
            </details>
          );
        }

        return (
          <div key={`g-orphan-${gi}`} className="space-y-2">
            {g.children.map((c) => renderSiteBlock(c.site.id, c.site.site_name, 1))}
          </div>
        );
      })}
    </div>
  );
}
